import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, ArrowRight, Mail, CheckCircle2, Star, Phone, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";
import { PhoneInput } from "@/components/ui/phone-input";
import { trackFacebookEvent } from "@/utils/facebookTracking";

const signUpSchema = z.object({
  organizationName: z.string().trim().min(2, "Organization name must be at least 2 characters").max(100, "Organization name must be less than 100 characters"),
  fullName: z.string().trim().min(2, "Name must be at least 2 characters"),
  email: z.string().trim().email("Invalid email address"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  phoneNumber: z.string().min(6, "Phone number must be at least 6 digits"),
});

export default function Signup() {
  const { user, signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [alreadyRegisteredEmail, setAlreadyRegisteredEmail] = useState<string | null>(null);

  const [signUpData, setSignUpData] = useState({
    organizationName: "",
    fullName: "",
    email: "",
    password: "",
    phoneCountryCode: "+91",
    phoneNumber: "",
  });

  // Capture tracking parameters on mount
  useEffect(() => {
    const params: Record<string, string> = {};
    const trackingKeys = ['fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'];
    trackingKeys.forEach(key => {
      const val = searchParams.get(key);
      if (val) params[key] = val;
    });
    if (Object.keys(params).length > 0) {
      try { sessionStorage.setItem('tracking_params', JSON.stringify(params)); } catch { /* ignore */ }
    }
  }, [searchParams]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setAlreadyRegisteredEmail(null);

    try {
      signUpSchema.parse(signUpData);
      await proceedWithSignUp();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else if (error?.message?.toLowerCase().includes("already registered")) {
        setAlreadyRegisteredEmail(signUpData.email);
      }
    }
  };

  const proceedWithSignUp = async () => {
    setIsLoading(true);
    try {
      const result = await signUp({
        email: signUpData.email,
        password: signUpData.password,
        fullName: signUpData.fullName,
        phoneCountryCode: signUpData.phoneCountryCode,
        phoneNumber: signUpData.phoneNumber,
        organizationName: signUpData.organizationName,
      });

      if (result.success) {
        const nameParts = signUpData.fullName.trim().split(' ');
        trackFacebookEvent('CompleteRegistration', {
          email: signUpData.email,
          firstName: nameParts[0],
          lastName: nameParts.slice(1).join(' ') || undefined,
          phone: signUpData.phoneCountryCode + signUpData.phoneNumber,
        }, {
          content_name: 'User Registration Complete',
        });

        if (result.needsEmailConfirmation) {
          setSignUpEmail(signUpData.email);
          setSignUpSuccess(true);
        } else {
          navigate("/onboarding");
        }
      }
    } catch (error: any) {
      if (error?.message?.toLowerCase().includes("already registered")) {
        setAlreadyRegisteredEmail(signUpData.email);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    const targetEmail = signUpEmail;
    if (!targetEmail) return;

    try {
      setIsLoading(true);
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: targetEmail,
        options: { emailRedirectTo: redirectUrl },
      });
      if (error) throw error;

      toast.success("Verification email sent!");
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      toast.error("Failed to resend: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] w-full flex bg-background">
      {/* Left Side - Brand & Features (Hidden on Mobile) */}
      <div className="hidden lg:flex w-1/2 relative bg-white text-brand-950 overflow-hidden flex-col justify-between p-16 border-r border-brand-100">
        {/* Background Gradients */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-brand-100/40 via-white to-white z-0"></div>
        <div className="absolute top-[-10%] right-[-20%] w-[700px] h-[700px] bg-brand-200/20 rounded-full blur-[120px] z-0 mix-blend-multiply animate-pulse duration-10000"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-200/20 rounded-full blur-[100px] z-0 opacity-70 mix-blend-multiply"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.02] z-0 mix-blend-multiply"></div>

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <img src="/logo.png" alt="WDC Today" className="h-10 w-auto object-contain" />
          <span className="text-xl font-heading font-bold tracking-tight text-brand-900">WDC Today</span>
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-xl">
          <h1 className="text-6xl font-heading font-bold leading-[1.1] tracking-tight mb-8 text-brand-950">
            Grow your network,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-700 via-brand-600 to-brand-700 animate-gradient-x">effortlessly.</span>
          </h1>
          <p className="text-lg text-brand-900/80 leading-relaxed font-medium max-w-md mb-12">
            The <span className="text-brand-950 font-bold">easiest cloud-based tool</span> for scaling your LinkedIn outreach with human-like precision.
          </p>

          {/* Social Proof Card (WDC main domain only) */}
          {(
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-500/20 to-teal-accent-500/20 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
              <div className="relative flex items-center gap-5 p-5 rounded-2xl bg-white/70 border border-brand-100 shadow-sm backdrop-blur-xl hover:bg-white/90 transition-colors">
                <div className="flex -space-x-2 shrink-0">
                  {[
                    { name: "Tata", src: "/logos/tata.png" },
                    { name: "Emami", src: "/logos/emami.png" },
                    { name: "Slack", src: "/logos/slack.svg" },
                    { name: "Volkswagen", src: "/logos/volkswagen.png" }
                  ].map((logo, index) => (
                    <div
                      key={logo.name}
                      className={`relative w-10 h-10 rounded-full border-2 border-white bg-white flex items-center justify-center ring-2 ring-brand-50 overflow-hidden ${logo.name === 'Emami' ? 'p-0.5' : 'p-1.5'}`}
                      style={{ zIndex: 10 - index }}
                    >
                      <img src={logo.src} alt={logo.name} className="w-full h-full object-contain" />
                    </div>
                  ))}
                </div>
                <div className="h-8 w-px bg-zinc-200"></div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-brand-700/70 font-medium">
                    Loved by <span className="text-brand-900 font-bold">2,000+</span> growth teams
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Badge */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 border border-brand-100 text-xs font-medium text-brand-600">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-accent-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-accent-500"></span>
            </span>
            System Operational
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12 bg-background overflow-y-auto">
        <div className="w-full max-w-[400px] space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 my-auto">

          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-2">
            <img src="/logo.png" alt="WDC Today" className="h-12 w-auto object-contain" />
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-brand-950">Get Started</h2>
            <p className="text-muted-foreground">
              Create your account to start growing your network
            </p>
          </div>

          {alreadyRegisteredEmail ? (
            <div className="text-center space-y-6 py-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="w-20 h-20 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shadow-sm">
                  <AlertCircle className="w-6 h-6" />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-foreground tracking-tight">Account already exists</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-[280px] mx-auto">
                  An account with <span className="font-semibold text-foreground">{alreadyRegisteredEmail}</span> is already registered.
                </p>
              </div>
              <div className="space-y-3 pt-2">
                <Button asChild className="w-full h-10 font-medium shadow-sm">
                  <Link to="/auth">Sign In Instead</Link>
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setAlreadyRegisteredEmail(null);
                    setSignUpData({ organizationName: "", fullName: "", email: "", password: "", phoneCountryCode: "+91", phoneNumber: "" });
                  }}
                  className="w-full h-10 text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  Use a different email
                </Button>
              </div>
            </div>
          ) : signUpSuccess ? (
            <div className="text-center space-y-6 py-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 shadow-sm">
                  <Mail className="w-6 h-6" />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-foreground tracking-tight">Check your inbox</h3>
                <p className="text-muted-foreground text-sm">
                  We've sent a verification link to<br />
                  <span className="font-medium text-foreground">{signUpEmail}</span>
                </p>
              </div>
              <div className="bg-muted/50 border border-border rounded-lg p-5 text-left space-y-3 mx-1">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Next steps:
                </div>
                <ol className="text-sm text-muted-foreground space-y-3 list-decimal list-inside pl-1">
                  <li className="pl-1">Open your email inbox <span className="text-xs text-muted-foreground/70">(check spam too)</span></li>
                  <li className="pl-1">Click <span className="font-medium text-foreground">"Confirm your email"</span></li>
                  <li className="pl-1">Come back here to sign in</li>
                </ol>
              </div>
              <div className="space-y-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0 || isLoading}
                  className="w-full h-10 border-input text-foreground hover:bg-muted"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Verification Email"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSignUpSuccess(false);
                    setSignUpEmail("");
                    setSignUpData({ organizationName: "", fullName: "", email: "", password: "", phoneCountryCode: "+91", phoneNumber: "" });
                    setResendCooldown(0);
                  }}
                  className="w-full h-10 text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  Back to Sign Up
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSignUp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="signup-org" className="text-sm font-medium ml-1">Organization Name</Label>
                <Input
                  id="signup-org"
                  placeholder="Acme Inc."
                  value={signUpData.organizationName}
                  onChange={(e) => setSignUpData({ ...signUpData, organizationName: e.target.value })}
                  className={`h-10 px-3 bg-background border-input focus:bg-background transition-all ${errors.organizationName ? "border-destructive focus-visible:ring-destructive" : ""}`}
                />
                {errors.organizationName && <p className="text-xs text-destructive flex items-center gap-1.5 ml-1 font-medium"><AlertCircle className="w-3.5 h-3.5" /> {errors.organizationName}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-name" className="text-sm font-medium ml-1">Full Name</Label>
                <Input
                  id="signup-name"
                  placeholder="John Doe"
                  value={signUpData.fullName}
                  onChange={(e) => setSignUpData({ ...signUpData, fullName: e.target.value })}
                  className={`h-10 px-3 bg-background border-input focus:bg-background transition-all ${errors.fullName ? "border-destructive focus-visible:ring-destructive" : ""}`}
                />
                {errors.fullName && <p className="text-xs text-destructive flex items-center gap-1.5 ml-1 font-medium"><AlertCircle className="w-3.5 h-3.5" /> {errors.fullName}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-email" className="text-sm font-medium ml-1">Work Email</Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="name@company.com"
                  value={signUpData.email}
                  onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                  className={`h-10 px-3 bg-background border-input focus:bg-background transition-all ${errors.email ? "border-destructive focus-visible:ring-destructive" : ""}`}
                />
                {errors.email && <p className="text-xs text-destructive flex items-center gap-1.5 ml-1 font-medium"><AlertCircle className="w-3.5 h-3.5" /> {errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password" className="text-sm font-medium ml-1">Password</Label>
                <div className="relative">
                  <Input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a strong password"
                    value={signUpData.password}
                    onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                    className={`h-10 px-3 pr-10 bg-background border-input focus:bg-background transition-all ${errors.password ? "border-destructive focus-visible:ring-destructive" : ""}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <PasswordStrengthIndicator password={signUpData.password} />
                {errors.password && <p className="text-xs text-destructive flex items-center gap-1.5 ml-1 font-medium"><AlertCircle className="w-3.5 h-3.5" /> {errors.password}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium ml-1 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  Mobile Number
                </Label>
                <PhoneInput
                  countryCode={signUpData.phoneCountryCode}
                  phoneNumber={signUpData.phoneNumber}
                  onCountryCodeChange={(code) => setSignUpData({ ...signUpData, phoneCountryCode: code })}
                  onPhoneNumberChange={(phone) => setSignUpData({ ...signUpData, phoneNumber: phone })}
                  error={!!errors.phoneNumber}
                />
                {errors.phoneNumber && <p className="text-xs text-destructive flex items-center gap-1.5 ml-1 font-medium"><AlertCircle className="w-3.5 h-3.5" /> {errors.phoneNumber}</p>}
              </div>

              <Button
                type="submit"
                className="w-full h-10 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 mt-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white border-0"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Creating Account...</span>
                  </div>
                ) : (
                  <span className="flex items-center gap-2">
                    Create Account <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground px-4 leading-relaxed">
                By clicking continue, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground transition-colors">Terms</a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground transition-colors">Privacy Policy</a>.
              </p>
            </form>
          )}

          {/* Sign in link */}
          <div className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/auth" className="text-primary font-medium hover:underline">
              Sign In
            </Link>
          </div>

          {/* Mobile Social Proof (WDC main domain only) */}
          {(
            <div className="lg:hidden pt-6 border-t border-border/40 text-center space-y-6">
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Trusted by high-growth teams
                </p>
                <div className="flex items-center justify-center gap-10 opacity-60 grayscale">
                  {[
                    { name: "Tata", src: "/logos/tata.png" },
                    { name: "Emami", src: "/logos/emami.png" },
                    { name: "Volkswagen", src: "/logos/volkswagen.png" }
                  ].map((logo) => (
                    <div key={logo.name} className="flex items-center justify-center">
                      <img src={logo.src} alt={logo.name} className="h-8 w-auto object-contain" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
